import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { unwrapList } from '../lib/unwrap'

export interface AudienceGroup {
  id: string
  tenant_id: string
  name: string
  description?: string
  preset: 'generic' | 'school'
  created_at: string
}

export interface AudienceMember {
  id: string
  group_id: string
  user_id?: string
  display_name: string
  phone: string
  created_at: string
}

export interface AudienceSubscription {
  id: string
  subject_type: string
  subject_id: string
  event_type: string
  group_id?: string
  user_id?: string
}

// ── Audience groups ──────────────────────────────────────────────

export function useAudiences() {
  return useQuery({
    queryKey: ['audiences'],
    queryFn: () => api.get('/audiences').then((r) => unwrapList<AudienceGroup>(r.data)),
  })
}

export function useCreateAudience() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; description?: string; preset: 'generic' | 'school' }) =>
      api.post('/audiences', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['audiences'] }),
  })
}

export function useUpdateAudience() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name: string; description?: string; preset: 'generic' | 'school' }) =>
      api.put(`/audiences/${id}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['audiences'] }),
  })
}

export function useDeleteAudience() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/audiences/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['audiences'] }),
  })
}

// ── Audience members ─────────────────────────────────────────────

export function useAudienceMembers(groupId: string) {
  return useQuery({
    queryKey: ['audience-members', groupId],
    queryFn: () => api.get(`/audiences/${groupId}/members`).then((r) => unwrapList<AudienceMember>(r.data)),
    enabled: !!groupId,
  })
}

export function useAddAudienceMember(groupId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { user_id?: string; display_name: string; phone?: string }) =>
      api.post(`/audiences/${groupId}/members`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['audience-members', groupId] }),
  })
}

export function useRemoveAudienceMember(groupId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (memberId: string) => api.delete(`/audiences/${groupId}/members/${memberId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['audience-members', groupId] }),
  })
}

// ── Audience subscriptions (subject → audience/user routing) ────

export function useSubjectSubscriptions(subjectType: string, subjectId: string) {
  return useQuery({
    queryKey: ['audience-subscriptions', subjectType, subjectId],
    queryFn: () =>
      api
        .get('/audience-subscriptions', { params: { subject_type: subjectType, subject_id: subjectId } })
        .then((r) => unwrapList<AudienceSubscription>(r.data)),
    enabled: !!subjectId,
  })
}

export function useCreateSubscription() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { subject_type: string; subject_id: string; event_type: string; group_id?: string; user_id?: string }) =>
      api.post('/audience-subscriptions', data).then((r) => r.data),
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ['audience-subscriptions', v.subject_type, v.subject_id] }),
  })
}

export function useDeleteSubscription() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id }: { id: string; subject_type: string; subject_id: string }) =>
      api.delete(`/audience-subscriptions/${id}`),
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ['audience-subscriptions', v.subject_type, v.subject_id] }),
  })
}
